import EnvironmentTag from '@/components/EnvironmentTag.jsx';
import Tag from '@/components/Tag.jsx';
import TextWithEllipsis from '@/components/TextWithEllipsis';
import RowV2 from '@/components/v2/RowV2';
import selectors from '@/redux/selectors/selectors';
import { differenceInSeconds } from 'date-fns';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import workflowScreenSelectors from '../redux/selectors/workflowScreenSelectors';
import setQuery from '@/lib/setQuery';
import StatusMarker from '@/components/modals/workflowLogsModal/StatusMarker';
import { formatDuration } from '@shared/utils/formatDuration.js';
import { formatShortTimestamp } from '@shared/utils/formatShortTimestamp.js';
import { workflowStatusMap } from '@shared/schema/workflowStatusMap.js';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import { query } from '@/redux/selectors/querySelectors';
import utilityAppIcons from '@/screens/workflows/schema/utilityAppIcons';
import IconV2 from '@/components/v2/IconV2';
import getLogTriggerMessage from './lib/getLogTriggerMessage';

const statusDisplayMap = {
  ...workflowStatusMap,
  6: 'Limit Exceeded'
};

const LogEntry = React.memo(({ log }) => {
  const { statusId, startedOn, finishedOn, updatedOn, errorCount, warningCount, changeCount, infoCount } = log;
  const activeLogId = useArgSelector(query, 'logId');
  const workflowSnapshotVersion = useSelector((state) => workflowScreenSelectors.activeWorkflowSnapshots(state)?.find((snapshot) => snapshot.id === log?.snapshotId)?.version);
  const environments = useSelector(selectors.environments) || [];
  const environment = useMemo(() => environments.find((e) => e.id === log?.environmentId), [JSON.stringify(environments), log?.environmentId]);

  const isRunning = log?.startedOn && !log?.finishedOn && log?.statusId === 1;
  const duration = differenceInSeconds(new Date(finishedOn || updatedOn), new Date(startedOn));
  const timeAgo = formatShortTimestamp(startedOn);
  const environmentLabel = environment?.name || '';
  const versionLabel = workflowSnapshotVersion || 'Draft';
  const environmentName = `${`${environmentLabel || 'Production'} - `}${versionLabel}`;
  const environmentDataTitle = `${environmentLabel ? `${environmentLabel} Environment - ` : ''}Workflow Version ${versionLabel}`;

  const summary = statusDisplayMap[statusId] || 'Unknown Status';
  const parsedChangeCount = parseInt(changeCount || '0', 10);
  const parsedErrorCount = parseInt(errorCount || '0', 10);
  const parsedWarningCount = parseInt(warningCount || '0', 10);
  const parsedInfoCount = parseInt(infoCount || '0', 10);

  const triggerStep = log?.meta?.triggerStep;
  const triggerStepMessage = triggerStep ? getLogTriggerMessage(triggerStep) : null;

  const handleRowClick = () => {
    if (activeLogId === log.id) {
      return;
    }

    setQuery({ logId: log.id, time: 0, logStepId: null, logTab: null, workflowVersion: workflowSnapshotVersion });
  };

  return (
    <RowV2 className={`log-entry`} onClick={handleRowClick} active={activeLogId === log?.id}>
      <RowV2.Left>
        <div className="status-container">
          <StatusMarker log={log} />
          <TextWithEllipsis className={`entry-message ${!duration ? 'empty' : ''}`} data-title={log?.summary}>
            <span key="status">{summary}</span>
            {parsedChangeCount > 0 && (
              <span key="changes" className="change-count" data-title={`${parsedChangeCount} changes`}>
                {parsedChangeCount > 99 ? '99+' : parsedChangeCount}
              </span>
            )}
            {parsedErrorCount > 0 && (
              <span key="errors" className="error-count" data-title={`${parsedErrorCount} errors`}>
                {parsedErrorCount > 99 ? '99+' : parsedErrorCount}
              </span>
            )}
            {parsedWarningCount > 0 && (
              <span key="warnings" className="warning-count" data-title={`${parsedWarningCount} warnings`}>
                {parsedWarningCount > 99 ? '99+' : parsedWarningCount}
              </span>
            )}
            {parsedInfoCount > 0 && (
              <span key="info" className="info-count" data-title={`${parsedInfoCount} info`}>
                {parsedInfoCount > 99 ? '99+' : parsedInfoCount}
              </span>
            )}
          </TextWithEllipsis>
        </div>
      </RowV2.Left>
      <RowV2.Right>
        {timeAgo && (
          <TextWithEllipsis className={'time-ago'} title={new Date(startedOn).toLocaleDateString('en-US', { hour: 'numeric', minute: 'numeric' })}>
            {timeAgo}
          </TextWithEllipsis>
        )}
        <Tag>{`${isRunning ? 'Running for ' : ''}${formatDuration(duration)}`}</Tag>
        {triggerStep?.appSlug && <IconV2 icon={utilityAppIcons[triggerStep?.appSlug]} className="light-medium" size={12} title={triggerStepMessage} />}
        <EnvironmentTag environmentId={log?.environmentId} overrideName={environmentName} data-title={environmentDataTitle} />
        {/* {JSON.stringify(log.meta).includes('workflow-runner-fargate-staging') && <Tag variant="info">Staging</Tag>} */}
      </RowV2.Right>
    </RowV2>
  );
});

LogEntry.displayName = 'LogEntry';

export default LogEntry;
